/**
 * Laying a flat list of organizational units out as the tree it is.
 *
 * A picker that lists units alphabetically is unreadable in a real
 * organigram, because the levels carry the same name on purpose: the root,
 * the company beneath it and the profit centre beneath that are all called
 * "My Organization". Three identical lines, one of which leads somewhere.
 * Shown as a tree they are three different places at a glance.
 */

/** A unit as the ordering needs to see it. */
export interface UnitInTree {
  id: string;
  name: string;
  parentId: string | null;
}

/** One unit and how deep it sits; the root is zero. */
export interface OrderedUnit<T extends UnitInTree> {
  unit: T;
  depth: number;
}

/**
 * How far down the walk goes before it stops.
 *
 * Not a cycle guard — a unit has exactly one parent, so a cycle is closed on
 * itself and no root leads into it; the walk can never enter one. It is a
 * depth cap for an organigram nested deeper than anybody builds, which would
 * otherwise go that many frames deep. Nothing is lost when it bites: what
 * the walk did not reach is appended afterwards.
 */
const MAX_DEPTH = 64;

/**
 * The units depth-first, parents before their children, siblings by name.
 *
 * Every unit comes out exactly once, including ones whose parent is missing
 * from the list — a list filtered to one tenant can hold a unit whose parent
 * was filtered away, and dropping it would make it unreachable rather than
 * merely misplaced. Those stand at the top level, where they can at least be
 * seen and chosen.
 */
export function orderUnitsAsTree<T extends UnitInTree>(
  units: readonly T[],
): OrderedUnit<T>[] {
  const present = new Set(units.map((unit) => unit.id));
  const childrenOf = new Map<string | null, T[]>();

  for (const unit of units) {
    // A parent outside the list is no parent here.
    const key =
      unit.parentId && present.has(unit.parentId) ? unit.parentId : null;
    const siblings = childrenOf.get(key);
    if (siblings) siblings.push(unit);
    else childrenOf.set(key, [unit]);
  }

  for (const siblings of childrenOf.values()) {
    siblings.sort(
      (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
    );
  }

  const ordered: OrderedUnit<T>[] = [];
  const placed = new Set<string>();

  const walk = (parentId: string | null, depth: number): void => {
    if (depth > MAX_DEPTH) return;
    for (const unit of childrenOf.get(parentId) ?? []) {
      placed.add(unit.id);
      ordered.push({ unit, depth });
      walk(unit.id, depth + 1);
    }
  };

  walk(null, 0);

  // What the walk never reached — a closed cycle, or a branch past the depth
  // cap — still belongs in the list. A unit the picker never offers cannot
  // be chosen at all, and being shown at the top beats not being shown.
  for (const unit of units) {
    if (!placed.has(unit.id)) ordered.push({ unit, depth: 0 });
  }

  return ordered;
}

/**
 * The indent for a depth, as a run of non-breaking spaces.
 *
 * A native `<option>` collapses ordinary spaces and cannot be styled, so the
 * indentation has to be in the text itself. `optgroup` is no help: it nests
 * one level and an organigram nests four.
 */
export function treeIndent(depth: number, perLevel = 3): string {
  return '\u00a0'.repeat(Math.max(0, depth) * perLevel);
}
